
import React, { useState } from 'react';
import { SparcInteraction } from '@/services/agentic/sparcSessionService';
import { formatDistanceToNow } from 'date-fns';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Copy, Clock, ChevronDown, ChevronUp, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';

interface TimelineItemProps {
  interaction: SparcInteraction;
  isFirst: boolean;
  isLast: boolean;
  view: 'compact' | 'detailed';
}

const TimelineItem: React.FC<TimelineItemProps> = ({ 
  interaction, 
  isFirst, 
  isLast,
  view
}) => {
  const [isOpen, setIsOpen] = useState(false);
  
  const formattedDate = interaction.created_at 
    ? formatDistanceToNow(new Date(interaction.created_at), { addSuffix: true }) 
    : 'Unknown time';
  
  const copyToClipboard = (text: string, type: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${type} copied to clipboard`);
  };

  // Determine success/failure status based on response content
  const hasError = interaction.response.toLowerCase().includes('error') || 
                   interaction.response.toLowerCase().includes('failed') ||
                   interaction.metadata?.status === 'error';
  
  if (view === 'compact') {
    return (
      <div className={`
        flex items-center p-2 hover:bg-accent/40 rounded-md transition-colors
        ${hasError ? 'border-l-2 border-destructive/50' : ''}
      `}>
        <div className="mr-2 relative">
          <div className={`
            w-3 h-3 rounded-full ${hasError ? 'bg-destructive' : 'bg-primary'}
          `}></div>
          {!isLast && <div className="absolute top-3 left-1.5 w-0.5 h-full -ml-px bg-muted-foreground/20"></div>}
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center">
            <h4 className="font-medium text-sm truncate">
              {interaction.task_name || 'Unknown Task'}
            </h4>
            <span className="text-xs text-muted-foreground ml-2 flex items-center whitespace-nowrap">
              <Clock className="h-3 w-3 mr-1" />
              {formattedDate}
            </span>
          </div>
          <p className="text-xs text-muted-foreground truncate">{interaction.query}</p>
        </div>
        
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-xs">
            {interaction.provider}
          </Badge>
          {hasError ? (
            <XCircle className="h-4 w-4 text-destructive" />
          ) : (
            <CheckCircle className="h-4 w-4 text-primary" />
          )}
        </div>
      </div>
    );
  }
  
  // Detailed view
  return (
    <Collapsible
      open={isOpen}
      onOpenChange={setIsOpen}
      className={`
        border rounded-lg overflow-hidden
        ${hasError ? 'border-destructive/50' : 'border-border'}
      `}
    >
      <div className="flex items-center p-3">
        <div className="mr-3">
          <div className={`
            w-4 h-4 rounded-full 
            ${hasError ? 'bg-destructive' : 'bg-primary'}
          `}></div>
        </div>
        
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between">
            <h4 className="font-medium truncate">
              {interaction.task_name || 'Unknown Task'}
            </h4>
            <div className="flex items-center gap-2">
              <Badge variant="outline">
                {interaction.provider}
              </Badge>
              <Badge>{interaction.model}</Badge>
              <span className="text-xs text-muted-foreground whitespace-nowrap flex items-center">
                <Clock className="h-3 w-3 mr-1" />
                {formattedDate}
              </span>
            </div>
          </div>
        </div>
        
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="ml-2">
            {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
        </CollapsibleTrigger>
      </div>
      
      <CollapsibleContent>
        <div className="p-3 pt-0 space-y-3">
          <div className="bg-muted p-3 rounded-md">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium">Query</span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5" 
                onClick={() => copyToClipboard(interaction.query, 'Query')}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-sm whitespace-pre-wrap">{interaction.query}</p>
          </div>
          
          <div className={`p-3 rounded-md ${hasError ? 'bg-destructive/10' : 'bg-muted'}`}>
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm font-medium">Response</span>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-5 w-5" 
                onClick={() => copyToClipboard(interaction.response, 'Response')}
              >
                <Copy className="h-3 w-3" />
              </Button>
            </div>
            <p className="text-sm whitespace-pre-wrap">{interaction.response}</p>
          </div>
          
          {interaction.metadata && (
            <div className="text-xs text-muted-foreground grid grid-cols-2 gap-2">
              {interaction.metadata.executionTime && (
                <div>Execution time: {interaction.metadata.executionTime}ms</div>
              )}
              {interaction.metadata.inputTokens && (
                <div>Input tokens: ~{interaction.metadata.inputTokens}</div>
              )}
              {interaction.metadata.outputTokens && (
                <div>Output tokens: ~{interaction.metadata.outputTokens}</div>
              )}
              {interaction.metadata.timestamp && (
                <div>Timestamp: {new Date(interaction.metadata.timestamp).toLocaleString()}</div>
              )}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export default TimelineItem;
